import { Component, HostListener } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Subscription, filter } from 'rxjs';
import { GoogleAnalyticsService } from './core/analytics/googleAnalytics.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './core/local-storage/local-storage.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  previousPath: string = '';

  constructor(
    private config: PrimeNGConfig,
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
  ) {
    this.initTranslations();
  }

  beforeinstallpromptEvent: any;
  private routerEventsSubscription: Subscription | undefined;
  private readonly PWA_INSTALL_DISMISSED_KEY = 'pwa-install-dismissed';
  private readonly DISMISS_DURATION_DAYS = 30;

  get isPublicRoute(): boolean {
    return this.router.url.startsWith('/public');
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  // Handle "add to home screen" proposal
  onBeforeinstallprompt(event: any) {
    //Only for mobile devices
    //if (!window.matchMedia('(display-mode: standalone)').matches) {
    //  return;
    //}

    // Vérifier si l'utilisateur a récemment refusé l'installation
    if (this.isPWAInstallRecentlyDismissed()) {
      event.preventDefault();
      return;
    }

    this.beforeinstallpromptEvent = event;
    event.preventDefault();
  }

  ngOnInit() {
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.beforeinstallpromptEvent = undefined;
      }
      if (event instanceof NavigationEnd) {
        const matchedRoute = this.getMatchedRoutePattern();
        this.googleAnalyticsService.pageView(event.urlAfterRedirects, matchedRoute);

        // If the route has changed reset the scroll position to the top
        // do not take into account query string (used for charts view params)
        const currentPath = event.urlAfterRedirects.split('?')[0]; // Get path without query string
        if (currentPath !== this.previousPath) {
          window.scrollTo(0, 0); // Reset scroll position
          this.previousPath = currentPath; // Update previous path
        }
      }
    });
  }

  private getMatchedRoutePattern(): string {
    let route = this.router.routerState.snapshot.root;
    let matchedPattern = '';

    while (route.firstChild) {
      route = route.firstChild;
      if (route.routeConfig && route.routeConfig.path) {
        matchedPattern += `/${route.routeConfig.path}`;
      }
    }

    return matchedPattern;
  }

  ngOnDestroy() {
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }

  initTranslations() {
    // Load PrimeNG translations from ngx-translate
    this.translateService.get('primeng').subscribe((translations) => {
      this.config.setTranslation(translations);
    });
  }

  /**
   * Vérifie si l'utilisateur a refusé l'installation PWA récemment (moins d'un mois)
   */
  private isPWAInstallRecentlyDismissed(): boolean {
    const dismissedDate = this.localStorageService.get<string>(this.PWA_INSTALL_DISMISSED_KEY);
    if (!dismissedDate) {
      return false;
    }

    const dismissedTime = new Date(dismissedDate).getTime();
    const currentTime = new Date().getTime();
    const daysSinceDismissed = (currentTime - dismissedTime) / (1000 * 60 * 60 * 24);

    return daysSinceDismissed < this.DISMISS_DURATION_DAYS;
  }

  /**
   * Enregistre la date de refus de l'installation PWA
   */
  dismissPWAInstall() {
    this.localStorageService.save(this.PWA_INSTALL_DISMISSED_KEY, new Date().toISOString());
    this.beforeinstallpromptEvent = undefined;
  }
}
